// DealPipelinePanel — the CRM revenue story: pipeline KPIs (total deals /
// amount / closed / forecast / projected) above a deal-stage funnel.
// Answers "how much revenue is in motion and where is it stuck."
//
// Depends on recharts, the app's named theme tokens, and ONE shared helper:
// primitives.formatMoney (single source of truth for money formatting,
// 2026-08-10 — this file's own local `formatK` duplicate is deleted, not
// just unused). No other shared-file imports/card/metric primitives — same
// convention as the other client-analytics frames in this directory.
//
// LABEL-OVERLAP FIX (2026-08-09): the funnel previously drew each stage's
// "name · $Xk" label directly on the shape via <LabelList position="right">.
// Real client pipelines run 10-12+ stages (confirmed: ALIBI Music has 12),
// and recharts' Funnel gives every item an EQUAL vertical slice of the
// chart height regardless of value — with 12 items in a 200px-tall chart
// that's ~16.6px per label, well under the ~14-16px a 12px label needs, so
// adjacent labels stacked on top of each other ("STEMS Request · $0k"
// printed over "Music Search · $356.9k"). Fixed by moving OFF on-shape
// labels entirely and onto a scrollable legend/table beside the chart —
// each stage gets its own row height there, so labels can never collide
// regardless of stage count. The funnel itself keeps its Tooltip for exact
// values on hover; the legend is the primary place to read them.

import {
  Bar,
  BarChart,
  Cell,
  Funnel,
  FunnelChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatLocalDateRange, formatMoney } from "@/components/client-analytics/primitives";
import type { CrmPeriodComparison, PipelineBreakdown } from "@/types/clientAnalytics";

export interface DealPipelineStage {
  name: string;
  value: number;
}

export interface DealPipelinePanelProps {
  /** Revenue already closed-won this period. */
  closed: number;
  /** Weighted forecast — deals likely to close. */
  forecast: number;
  /** Total projected value across the open pipeline. */
  projected: number;
  /** ISO currency code for formatting; defaults to USD. */
  currency?: string;
  /** Deal stages, ordered top (widest/earliest) to bottom (narrowest/latest). */
  stages: DealPipelineStage[];
  /** Total pipeline value across every stage (AA "Amount" KPI tile). */
  amount?: number | null;
  /** Total deal count across every stage (AA "Total Deals" KPI tile). */
  totalDeals?: number | null;
  /** The actual window this pipeline snapshot covers (ISO dates). */
  periodStart?: string | null;
  periodEnd?: string | null;
  /** Real period-over-period deltas for the KPI tiles; omitted entirely
   * (no pill) when a given field has no comparison data. */
  comparison?: CrmPeriodComparison | null;
  /** Per-pipeline deal split (GAP 2 fix, 2026-08-09) — one block per named
   * HubSpot pipeline (e.g. "Sales Pipeline", "Sales Pipeline - UK"), each
   * with its own deal count/amount KPIs and stage bar chart. Rendered below
   * the blended funnel above; omitted entirely when empty (non-HubSpot CRM,
   * or a client with zero deals in the window). */
  pipelines?: PipelineBreakdown[];
  /** Row count of the raw deals pull `pipelines` was built from (capped at
   * 500) — shown as "top N of {totalDeals}" when it's a genuine sample. */
  pipelinesSampleSize?: number | null;
}

// Sample data for standalone preview / storybook-style rendering.
export const demoPipeline: DealPipelinePanelProps = {
  closed: 8000,
  forecast: 16000,
  projected: 20000,
  currency: "USD",
  amount: 24000,
  totalDeals: 42,
  periodStart: "2026-05-01",
  periodEnd: "2026-08-08",
  stages: [
    { name: "Meeting", value: 20000 },
    { name: "Qualified", value: 16000 },
    { name: "Proposal", value: 12000 },
    { name: "Docs", value: 9000 },
    { name: "Closed-Won", value: 8000 },
  ],
  pipelinesSampleSize: 500,
  pipelines: [
    { name: "Sales Pipeline", total_deals: 386, amount: 637174, closed_amount: 86049, stages: [
      { label: "Music Search", value: 200000 },
      { label: "Blanket Search", value: 150000 },
      { label: "Music Licensed", value: 87174 },
    ] },
    { name: "eComm Subscription Pipeline", total_deals: 65, amount: 3486, closed_amount: 2933, stages: [
      { label: "Subscription Started", value: 2200 },
      { label: "Subscription Renewed", value: 1286 },
    ] },
    { name: "eCommerce License Purchase", total_deals: 37, amount: 12649, closed_amount: 12649, stages: [
      { label: "Purchased", value: 12649 },
    ] },
    { name: "Sales Pipeline - UK", total_deals: 9, amount: 0, closed_amount: 0, stages: [
      { label: "Blanket Search", value: 0 },
    ] },
    { name: "Sales Pipeline - Foreign (Non-US Non-UK)", total_deals: 3, amount: 39053, closed_amount: 53, stages: [
      { label: "Blanket Search", value: 39053 },
    ] },
  ],
};

const CHART_HEIGHT = 200;
const PIPELINE_CHART_HEIGHT = 140;
const PIPELINE_ROW_HEIGHT = 24;
const LEGEND_ROW_HEIGHT = 22;
const LEGEND_MAX_HEIGHT = 260;

// Azure -> deep-azure gradient across stages: `#4CC9F0` (Core brand accent
// blue) fading to `#074E6F` (Core primary/deep-azure). Whole hex, not CSS
// vars, because these are per-datum SVG fills interpolated at render time —
// same rationale as the `--chart-series-1` comment in index.css.
const GRADIENT_START = { r: 0x4c, g: 0xc9, b: 0xf0 };
const GRADIENT_END = { r: 0x07, g: 0x4e, b: 0x6f };

function stageColor(index: number, total: number): string {
  const t = total > 1 ? index / (total - 1) : 0;
  const lerp = (a: number, b: number) => Math.round(a + (b - a) * t);
  const r = lerp(GRADIENT_START.r, GRADIENT_END.r);
  const g = lerp(GRADIENT_START.g, GRADIENT_END.g);
  const b = lerp(GRADIENT_START.b, GRADIENT_END.b);
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}

// === Non-revenue pipeline filter (Carl's ruling, 2026-08-10) ===
// Live case: Tyler McBroom's HubSpot account has 7 pipelines behind ONE
// blended funnel -- two real sales pipelines (Sales Pipeline, Deep Dive Tax
// Plans) and FIVE "Recruiting - ..." pipelines reusing HubSpot's generic
// "deal" object as an applicant tracker (Application Received / Schedule
// Initial Screen / Rejected -- never a dollar in any stage, any period).
// Blended together that's a 31-stage funnel, ~20 of them permanently $0:
// unreadable, and it inflates "Total Deals" with applicant rows that were
// never marketing-driven revenue.
//
// NEVER name-match "Recruiting" -- the next client names their ATS
// pipeline something else and the filter silently does nothing. Filter on
// the data instead, in two steps:
//   1. DIRECT revenue: a pipeline carries dollar value if `amount`,
//      `closed_amount`, or any of its own stages is nonzero THIS period.
//   2. SHARED-VOCABULARY revenue: a pipeline with $0 everywhere THIS
//      period can still be a genuine sales pipeline having a quiet
//      stretch -- confirmed live on ALIBI Music's "Sales Pipeline - UK"
//      (amount=0, closed_amount=0, every stage 0 this period), which
//      reuses the exact stage names ("Music Search", "Closed Unknown
//      Outcome") of ALIBI's main "Sales Pipeline", which DOES carry
//      revenue. Two pipelines sharing stage-name vocabulary are the same
//      kind of workflow; an applicant tracker's stage names never overlap
//      a sales pipeline's at all (confirmed live: zero shared labels
//      between Tyler McBroom's 2 revenue pipelines and 5 recruiting
//      pipelines). Naive "drop every $0-amount pipeline" would have
//      wrongly dropped ALIBI's UK pipeline -- this is why.
//
// Fail-open: if NO pipeline has direct revenue this period (a totally
// quiet account), nothing gets excluded -- there's no revenue signal left
// to filter against, so filtering would just hide everything.
function pipelineHasDirectRevenue(p: PipelineBreakdown): boolean {
  return p.amount > 0 || p.closed_amount > 0 || p.stages.some((s) => s.value > 0);
}

interface PipelineClassification {
  // Pipelines to actually render (cards + feeds the funnel filter below).
  revenuePipelines: PipelineBreakdown[];
  excludedCount: number;
  // Union of stage labels across every revenue pipeline -- used to filter
  // the blended top-level funnel (`stages` prop) down to the same set,
  // since that funnel has no pipeline identifier of its own to filter on
  // directly (see CrmSection's backend docstring).
  revenueStageLabels: Set<string>;
}

function classifyPipelines(pipelines: PipelineBreakdown[] | undefined): PipelineClassification {
  const all = pipelines ?? [];
  const directRevenue = all.filter(pipelineHasDirectRevenue);
  if (all.length === 0 || directRevenue.length === 0) {
    // Nothing to exclude against (no breakdown at all, or every pipeline
    // is quiet this period) -- fail open, keep everything.
    return {
      revenuePipelines: all,
      excludedCount: 0,
      revenueStageLabels: new Set(all.flatMap((p) => p.stages.map((s) => s.label))),
    };
  }
  const revenueStageLabels = new Set(directRevenue.flatMap((p) => p.stages.map((s) => s.label)));
  const revenuePipelines = all.filter(
    (p) => pipelineHasDirectRevenue(p) || p.stages.some((s) => revenueStageLabels.has(s.label)),
  );
  return {
    revenuePipelines,
    excludedCount: all.length - revenuePipelines.length,
    revenueStageLabels,
  };
}

// Labels the prior-period and year-over-year comparison ranges, so an
// off-by-one here mislabels what every delta is measured against. Parsing goes
// through the shared primitives.parseLocalDate — see its comment for why
// `new Date("YYYY-MM-DD")` rendered a day early west of UTC.
export function formatDateRange(start?: string | null, end?: string | null): string | null {
  return formatLocalDateRange(start, end);
}

// COMPARISON LABELING (2026-08-09): a bare "▼9.11%" tells you nothing about
// what it's measured against. AA always pairs a delta with an explicit
// basis ("| MoM" / "| YoY" report pages) and the exact prior window, so
// every badge here is captioned with its basis — "Prior period" for the
// backend's immediately-preceding equal-length window (not "MoM": the
// dashboard's window isn't always a calendar month) and "YoY" for the true
// calendar-year-shifted comparison (`comparison.year_over_year`) — each
// with its exact comparison range in a hover title. `year_over_year` is
// null/absent (never a fabricated 0%) for a client with no HubSpot deal
// data from a year ago; `buildDeltas` passes that straight through and
// ComparisonBadges drops the undefined slot silently rather than rendering
// a placeholder or falling back to the other basis mislabeled as YoY.
interface ComparisonDelta {
  basisLabel: string;
  pct: number | null | undefined;
  rangeLabel?: string | null;
}

// One badge per basis that actually has real data — a missing/undefined
// pct for a basis (e.g. no year-ago pull yet for this client) simply omits
// that badge rather than rendering a placeholder.
function ComparisonBadges({ deltas }: { deltas: ComparisonDelta[] }) {
  const visible = deltas.filter(
    (d): d is ComparisonDelta & { pct: number } =>
      d.pct !== null && d.pct !== undefined && Number.isFinite(d.pct),
  );
  if (visible.length === 0) return null;
  return (
    <div className="flex flex-col items-end gap-0.5">
      {visible.map((d) => {
        const tone = d.pct >= 0 ? "text-[hsl(var(--status-success))]" : "text-[hsl(var(--status-error))]";
        const arrow = d.pct >= 0 ? "▲" : "▼";
        return (
          <span
            key={d.basisLabel}
            className={`whitespace-nowrap text-dash-context font-semibold tabular ${tone}`}
            title={d.rangeLabel ? `${d.basisLabel} vs ${d.rangeLabel}` : d.basisLabel}
          >
            {arrow} {Math.abs(d.pct).toFixed(2)}%{" "}
            <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {d.basisLabel}
            </span>
          </span>
        );
      })}
    </div>
  );
}

function PipelineStat({
  label,
  value,
  deltas,
}: {
  label: string;
  value: string;
  deltas?: ComparisonDelta[];
}) {
  return (
    <div className="flex-1 flex flex-col gap-1">
      <div className="flex items-start justify-between gap-2">
        <span className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        {deltas && <ComparisonBadges deltas={deltas} />}
      </div>
      <span className="text-dash-value font-bold tabular text-foreground">
        {value}
      </span>
    </div>
  );
}

// One named pipeline's card — KPI row (deal count + amount) above a
// horizontal stage bar chart (screenshot reference: "five named pipelines",
// each with its own count/amount/stage breakdown). Bar, not funnel, per the
// architect brief — a per-pipeline funnel with as few as 1-2 stages (e.g.
// "eCommerce License Purchase") reads worse than a simple bar; the blended
// funnel above already owns the funnel visualization for the full-pipeline
// story.
function PipelineCard({
  pipeline,
  currency,
}: {
  pipeline: PipelineBreakdown;
  currency: string;
}) {
  const sortedStages = [...pipeline.stages].sort((a, b) => b.value - a.value);
  const chartHeight = Math.max(PIPELINE_CHART_HEIGHT, sortedStages.length * PIPELINE_ROW_HEIGHT);
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4 min-w-0">
      <div className="flex items-start justify-between gap-2">
        <span className="text-dash-context font-semibold text-foreground" title={pipeline.name}>
          {pipeline.name}
        </span>
      </div>
      <div className="flex flex-wrap divide-x divide-border">
        <div className="pr-4 flex flex-col gap-0.5">
          <span className="text-dash-label uppercase tracking-wide text-muted-foreground">Deals</span>
          <span className="text-dash-value font-bold tabular text-foreground">
            {pipeline.total_deals.toLocaleString()}
          </span>
        </div>
        <div className="pl-4 flex flex-col gap-0.5">
          <span className="text-dash-label uppercase tracking-wide text-muted-foreground">Amount</span>
          <span className="text-dash-value font-bold tabular text-foreground">
            {formatMoney(pipeline.amount, currency)}
          </span>
        </div>
      </div>
      {sortedStages.length > 0 ? (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart data={sortedStages} layout="vertical" margin={{ top: 4, right: 12, bottom: 0, left: 4 }}>
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "var(--color-muted-foreground)" }} />
            <YAxis
              type="category"
              dataKey="label"
              width={110}
              tick={{ fontSize: 11, fill: "var(--color-muted-foreground)" }}
            />
            <Tooltip
              contentStyle={{ borderRadius: 8, border: "1px solid var(--color-border)", fontSize: 12 }}
              formatter={(value: number) => [formatMoney(value, currency), "Amount"]}
            />
            <Bar dataKey="value" fill="var(--color-coral)" radius={[0, 4, 4, 0]} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="text-dash-context text-muted-foreground">No stage data</div>
      )}
    </div>
  );
}

export function DealPipelinePanel({
  closed,
  forecast,
  projected,
  currency = "USD",
  stages,
  amount,
  totalDeals,
  periodStart,
  periodEnd,
  comparison,
  pipelines,
  pipelinesSampleSize,
}: DealPipelinePanelProps) {
  // Non-revenue pipeline filter (see classifyPipelines' doc comment above) —
  // one classification pass feeds all three places task #2 calls out: the
  // funnel below (via revenueStageLabels), the KPI strip (via the
  // effective* recomputes below), and the per-pipeline cards (via
  // revenuePipelines replacing the raw `pipelines` prop).
  const { revenuePipelines, excludedCount, revenueStageLabels } = classifyPipelines(pipelines);

  // Only filter the blended funnel when there's something real to filter
  // against (pipelines were provided AND at least one was actually
  // excluded) -- otherwise `revenueStageLabels` reflects every pipeline
  // anyway (see classifyPipelines' fail-open branch) and this is a no-op.
  const displayStages =
    excludedCount > 0 ? stages.filter((s) => revenueStageLabels.has(s.name)) : stages;
  const hasStages = displayStages && displayStages.length > 0;
  const funnelData = hasStages
    ? displayStages.map((stage, i) => ({
        ...stage,
        fill: stageColor(i, displayStages.length),
      }))
    : [];

  // KPI-strip recompute: only trustworthy when `pipelines` is a COMPLETE
  // breakdown of every deal (not a truncated 500-row sample -- compare
  // against `pipelinesSampleSize` the same way the "Top N of M deals"
  // caption below does) AND something was actually excluded. Otherwise
  // pass the backend's own account-wide totals straight through unchanged
  // -- this is what keeps ALIBI Music (nothing excluded) byte-identical.
  // `closed` is derivable from PipelineBreakdown.closed_amount the same
  // way `amount` is; `forecast`/`projected` have no per-pipeline analogue
  // on PipelineBreakdown, so they're never recomputed here -- but since an
  // excluded pipeline is $0 everywhere by construction (see
  // pipelineHasDirectRevenue), it contributes $0 to any weighted
  // forecast/projected total anyway, so leaving them as backend pass-
  // through is not a correctness gap, just a field this endpoint doesn't
  // expose split out.
  const pipelineBreakdownIsComplete =
    pipelines != null &&
    pipelines.length > 0 &&
    (pipelinesSampleSize == null || totalDeals == null || pipelinesSampleSize >= totalDeals);
  const shouldRecomputeKpis = pipelineBreakdownIsComplete && excludedCount > 0;
  const effectiveTotalDeals = shouldRecomputeKpis
    ? revenuePipelines.reduce((sum, p) => sum + p.total_deals, 0)
    : totalDeals;
  const effectiveAmount = shouldRecomputeKpis
    ? revenuePipelines.reduce((sum, p) => sum + p.amount, 0)
    : amount;
  const effectiveClosed = shouldRecomputeKpis
    ? revenuePipelines.reduce((sum, p) => sum + p.closed_amount, 0)
    : closed;

  // Precision Restorations false-$0 fix (task #3): `contacts` can be
  // meaningful (200 rows) while `crm` itself has NO real pipeline data --
  // stages=[], total_deals=null, amount=null. Rendering "Closed $0k ·
  // Forecast $0k · Projected $0k" next to "No pipeline data" in that case
  // tells the client SEO produced zero pipeline value, which is false —
  // deals just aren't in this pull. Gate the KPI strip AND the funnel/
  // placeholder together on the same real-data check DealTablePanel
  // already self-hides on (dashboardPanels.tsx) — never render a
  // confident-looking "$0" next to an honest "no data" placeholder.
  const hasMeaningfulPipelineData =
    hasStages || (effectiveTotalDeals !== null && effectiveTotalDeals !== undefined);

  const dateRange = formatDateRange(periodStart, periodEnd);
  const legendHeight = Math.min(
    LEGEND_MAX_HEIGHT,
    Math.max(CHART_HEIGHT, funnelData.length * LEGEND_ROW_HEIGHT),
  );

  // The whole `comparison` object is one preceding-window pull, and
  // `comparison.year_over_year` (when present) is one calendar-year-shifted
  // pull — each shares one prior range across every stat, computed once
  // here rather than per-tile. `year_over_year` is null/absent for a
  // client with no HubSpot deal data from a year ago (never a fabricated
  // 0%) — ComparisonBadges drops that slot silently in that case.
  const priorRangeLabel = formatDateRange(comparison?.prior_period_start, comparison?.prior_period_end);
  const yoy = comparison?.year_over_year;
  const yoyRangeLabel = formatDateRange(yoy?.prior_period_start, yoy?.prior_period_end);
  function buildDeltas(
    priorPct: number | null | undefined,
    yoyPct: number | null | undefined,
  ): ComparisonDelta[] {
    return [
      { basisLabel: "Prior period", pct: priorPct, rangeLabel: priorRangeLabel },
      { basisLabel: "YoY", pct: yoyPct, rangeLabel: yoyRangeLabel },
    ];
  }

  return (
    <div className="flex flex-col gap-4">
      {dateRange && (
        <div className="text-dash-context text-muted-foreground">{dateRange}</div>
      )}
      {/* KPI strip + funnel/placeholder gated together on hasMeaningfulPipelineData
          (task #3) — never render a confident "$0" KPI row beside an honest
          "No pipeline data" placeholder for a client whose deals just
          weren't in this pull (Precision Restorations). */}
      {hasMeaningfulPipelineData && (
        <>
          <div className="flex flex-wrap divide-x divide-border rounded-lg border border-border bg-card px-4 py-3">
            {effectiveTotalDeals !== null && effectiveTotalDeals !== undefined && (
              <div className="pr-4">
                <PipelineStat
                  label="Total Deals"
                  value={effectiveTotalDeals.toLocaleString()}
                  deltas={buildDeltas(comparison?.total_deals_change_pct, yoy?.total_deals_change_pct)}
                />
              </div>
            )}
            {effectiveAmount !== null && effectiveAmount !== undefined && (
              <div className="px-4">
                <PipelineStat
                  label="Amount"
                  value={formatMoney(effectiveAmount, currency)}
                  deltas={buildDeltas(comparison?.amount_change_pct, yoy?.amount_change_pct)}
                />
              </div>
            )}
            <div className="px-4">
              <PipelineStat
                label="Closed"
                value={formatMoney(effectiveClosed, currency)}
                deltas={buildDeltas(comparison?.closed_change_pct, yoy?.closed_change_pct)}
              />
            </div>
            <div className="px-4">
              <PipelineStat
                label="Forecast"
                value={formatMoney(forecast, currency)}
                deltas={buildDeltas(comparison?.forecast_change_pct, yoy?.forecast_change_pct)}
              />
            </div>
            <div className="pl-4">
              <PipelineStat
                label="Projected"
                value={formatMoney(projected, currency)}
                deltas={buildDeltas(comparison?.projected_change_pct, yoy?.projected_change_pct)}
              />
            </div>
          </div>

          {hasStages ? (
            <div className="flex gap-4">
              <div className="flex-1 min-w-0">
                <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                  <FunnelChart>
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: "1px solid var(--color-border)",
                        fontSize: 12,
                      }}
                      formatter={(value: number, _name: string, item: { payload?: DealPipelineStage }) => [
                        formatMoney(value, currency),
                        item?.payload?.name ?? "Value",
                      ]}
                    />
                    <Funnel dataKey="value" data={funnelData} isAnimationActive={false}>
                      {funnelData.map((entry) => (
                        <Cell key={entry.name} fill={entry.fill} />
                      ))}
                    </Funnel>
                  </FunnelChart>
                </ResponsiveContainer>
              </div>
              {/* Legend/table beside the chart — every stage gets its own fixed
                  row height, so labels can never overlap no matter how many
                  stages the pipeline has (see module docstring). */}
              <ul
                className="w-[180px] shrink-0 space-y-1 overflow-y-auto pr-1"
                style={{ maxHeight: legendHeight }}
              >
                {funnelData.map((entry) => (
                  <li key={entry.name} className="flex items-center gap-2 min-w-0">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-sm"
                      style={{ background: entry.fill }}
                    />
                    <span className="truncate text-dash-context text-foreground" title={entry.name}>
                      {entry.name}
                    </span>
                    <span className="tabular ml-auto shrink-0 text-dash-context text-muted-foreground">
                      {formatMoney(entry.value, currency)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div
              className="flex items-center justify-center text-dash-context text-muted-foreground"
              style={{ height: CHART_HEIGHT }}
            >
              No pipeline data
            </div>
          )}
        </>
      )}

      {/* Per-pipeline breakdown (GAP 2 fix, 2026-08-09) — one card per named
          HubSpot pipeline that carries real dollar value (task #2's
          non-revenue filter above); omitted entirely when empty. */}
      {revenuePipelines.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
              Pipelines
            </span>
            {pipelinesSampleSize != null && totalDeals != null && pipelinesSampleSize < totalDeals && (
              <span className="text-dash-context text-muted-foreground">
                Top {pipelinesSampleSize.toLocaleString()} of {totalDeals.toLocaleString()} deals
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {revenuePipelines.map((pipeline) => (
              <PipelineCard key={pipeline.name} pipeline={pipeline} currency={currency} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
